import { useState } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/d75q2klr5g02gnd69lc0';

export default function SupportForm() {
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    referencia: '',
    consulta: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        body.append(key, value);
      });

      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (res.ok) {
        setSubmitStatus('success');
        setFormData({ nombre: '', email: '', telefono: '', referencia: '', consulta: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="soporte" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">

          {/* Left — info */}
          <div>
            <span className="inline-block bg-[#0A2E1F]/10 text-[#0A2E1F] text-xs font-bold uppercase tracking-wider px-4 py-1.5 rounded-full mb-6">
              Soporte Post-Instalación
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              ¿Tienes alguna duda sobre <span className="text-[#FF6B35]">tu instalación?</span>
            </h2>
            <p className="text-gray-600 text-base leading-relaxed mb-10 max-w-md">
              Escríbenos directamente y nuestro equipo técnico revisará tu consulta de forma privada. Solo nosotros recibiremos tu mensaje para darte la mejor atención personalizada.
            </p>

            <div className="space-y-5">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex-shrink-0 rounded-xl bg-[#FF6B35]/10 flex items-center justify-center">
                  <i className="ri-lock-password-line text-[#FF6B35] text-xl"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 text-sm mb-1">100% Privado</h4>
                  <p className="text-gray-500 text-sm">Tu consulta llega exclusivamente a nuestro correo. Nadie más la verá.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex-shrink-0 rounded-xl bg-[#FF6B35]/10 flex items-center justify-center">
                  <i className="ri-tools-line text-[#FF6B35] text-xl"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 text-sm mb-1">Atención Técnica Especializada</h4>
                  <p className="text-gray-500 text-sm">Nuestros instaladores certificados responderán con la solución adecuada.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex-shrink-0 rounded-xl bg-[#FF6B35]/10 flex items-center justify-center">
                  <i className="ri-mail-send-line text-[#FF6B35] text-xl"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 text-sm mb-1">Respuesta por Email</h4>
                  <p className="text-gray-500 text-sm">Te contestamos en el correo que nos facilites, de forma rápida y clara.</p>
                </div>
              </div>
            </div>

            <div className="mt-10">
              <img
                src="https://public.readdy.ai/ai/img_res/7a329dfbf0bbba14498bf8adc00ec162.jpg"
                alt="Soporte Técnico"
                className="w-full h-56 object-cover object-top rounded-2xl"
              />
            </div>
          </div>

          {/* Right — form */}
          <div className="bg-[#F5F1E8] rounded-3xl p-8 md:p-10">
            {submitStatus === 'success' ? (
              <div className="text-center py-16">
                <div className="w-20 h-20 bg-[#0A2E1F] rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-mail-check-line text-[#FFB800] text-4xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">¡Consulta Enviada!</h3>
                <p className="text-gray-600 text-sm mb-8 max-w-sm mx-auto">
                  Hemos recibido tu duda correctamente. Nuestro equipo técnico te responderá por email en la mayor brevedad posible.
                </p>
                <button
                  onClick={() => setSubmitStatus('idle')}
                  className="bg-[#0A2E1F] text-white px-8 py-3 rounded-lg text-sm font-semibold hover:bg-[#FF6B35] transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  Enviar otra consulta
                </button>
              </div>
            ) : (
              <form
                id="soporte-instalacion-form"
                data-readdy-form
                onSubmit={handleSubmit}
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-[#0A2E1F] rounded-xl flex items-center justify-center">
                    <i className="ri-question-answer-line text-[#FFB800] text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Consulta Técnica</h3>
                    <p className="text-gray-500 text-xs">Solo la empresa recibirá tu mensaje</p>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label htmlFor="support-nombre" className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">
                      Nombre *
                    </label>
                    <input
                      id="support-nombre"
                      name="nombre"
                      type="text"
                      required
                      value={formData.nombre}
                      onChange={handleChange}
                      placeholder="Tu nombre completo"
                      className="w-full bg-white border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#FFB800]/40 focus:border-[#FFB800] transition-all"
                    />
                  </div>
                  <div>
                    <label htmlFor="support-email" className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">
                      Email *
                    </label>
                    <input
                      id="support-email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="tu@email.com"
                      className="w-full bg-white border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#FFB800]/40 focus:border-[#FFB800] transition-all"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label htmlFor="support-telefono" className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">
                      Teléfono
                    </label>
                    <input
                      id="support-telefono"
                      name="telefono"
                      type="tel"
                      value={formData.telefono}
                      onChange={handleChange}
                      placeholder="+34 600 000 000"
                      className="w-full bg-white border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#FFB800]/40 focus:border-[#FFB800] transition-all"
                    />
                  </div>
                  <div>
                    <label htmlFor="support-referencia" className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">
                      Nº Referencia / Contrato
                    </label>
                    <input
                      id="support-referencia"
                      name="referencia"
                      type="text"
                      value={formData.referencia}
                      onChange={handleChange}
                      placeholder="Ej: JYA-2024-0312"
                      className="w-full bg-white border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#FFB800]/40 focus:border-[#FFB800] transition-all"
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <label htmlFor="support-consulta" className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">
                    Tu duda o consulta *
                  </label>
                  <textarea
                    id="support-consulta"
                    name="consulta"
                    rows={5}
                    required
                    maxLength={500}
                    value={formData.consulta}
                    onChange={handleChange}
                    placeholder="Describe aquí tu duda o incidencia sobre la instalación. Por ejemplo: comportamiento de los paneles, lecturas del inversor, consumo inesperado..."
                    className="w-full bg-white border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#FFB800]/40 focus:border-[#FFB800] transition-all resize-none"
                  />
                  <p className="text-xs text-gray-400 mt-1 text-right">{formData.consulta.length}/500</p>
                </div>

                {submitStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3 mb-4 flex items-center gap-2">
                    <i className="ri-error-warning-line text-lg"></i>
                    Ha ocurrido un error. Por favor, inténtalo de nuevo.
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#0A2E1F] text-white py-3.5 rounded-lg text-sm font-semibold hover:bg-[#FF6B35] transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-lg"></i>
                      Enviando consulta...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line text-lg"></i>
                      Enviar Consulta Privada
                    </>
                  )}
                </button>

                <div className="flex items-center justify-center gap-2 mt-4">
                  <i className="ri-lock-line text-gray-400 text-sm"></i>
                  <p className="text-xs text-gray-400">
                    Mensaje privado — solo lo verá el equipo de JyA Fotovoltaica
                  </p>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
